import React, { useState } from 'react';
import { List } from 'immutable';
import moment from 'moment';
import { twMerge } from 'tailwind-merge';

import { setPreventUnload } from 'flow/actions/app-actions';
import { removeSharedVoiceMessage } from 'flow/actions/pbx-actions';
import { AudioPlayer } from 'flow/components/AudioPlayers';
import CallAdaptiveButton from 'flow/components/CallAdaptiveButton';
import PbxVoiceMessageContextMenu from 'flow/components/context-menus/PbxVoiceMessageContextMenu';
import Icon from 'flow/components/ui-components/Icon';
import IconButton from 'flow/components/ui-components/IconButton';
import Tooltip from 'flow/components/ui-components/Tooltip';
import UserAvatar from 'flow/components/ui-components/UserAvatar';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { getOwnCountryCode } from 'flow/selectors/selectors';
import { VoicemailKey } from 'flow/types/EntityKeys';
import { TypedMap } from 'flow/types/TypedMap';
import { VoiceMessageActions } from 'flow/types/Voicemail';
import { PbxSharedVoiceMessage } from 'flow/types/VoiceMessage';
import {
    getContactAvatar,
    getDateDescription,
    sharedVoiceMessageUrlPath,
} from 'flow/utils';
import { getCallActionMarkedAsHandled } from 'flow/utils/call-records-utils';
import { getContactName } from 'flow/utils/contact-utils';
import {
    getLoggedCallSubtitle,
    getLoggedCallTitle,
} from 'flow/utils/logged-calls-utils';

interface Props {
    voicemailKey: VoicemailKey;
    voiceMessage: TypedMap<PbxSharedVoiceMessage>;
    voiceMessagesActions: TypedMap<
        Record<string, List<TypedMap<VoiceMessageActions>>>
    >;
}

const PbxSharedVoicemailItem = ({
    voiceMessage,
    voicemailKey,
    voiceMessagesActions,
}: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();

    const [contextMenuIsActive, setContextMenuIsActive] = useState(false);

    const ownCountryCode = useAppSelector(getOwnCountryCode);

    const callTitle = getLoggedCallTitle(voiceMessage, i18next, ownCountryCode);

    const getHandledInfo = () => {
        const voiceMessageAction = getCallActionMarkedAsHandled(
            voiceMessagesActions.get(voiceMessage.get('uniqueId')),
        );

        if (!voiceMessageAction) return null;

        const agentName =
            voiceMessageAction &&
            getContactName(voiceMessageAction.getIn(['agent', 'contact']));
        const actionTimeUserFriendly =
            voiceMessageAction &&
            getDateDescription(
                voiceMessageAction.get('timestamp'),
                i18next,
                true,
            );

        return `${i18next.t('Queue.History.HandledBy', {
            agent: agentName,
        })} ${actionTimeUserFriendly}`;
    };

    return (
        <div key={voiceMessage.get('uniqueId')} className="px-2">
            <div className="group flex gap-x-2 p-2">
                <UserAvatar
                    src={getContactAvatar(voiceMessage.get('contact'))}
                />

                <div className="flex w-full flex-col">
                    <div className="flex">
                        <div className="flex w-full flex-col gap-2 p-2">
                            <div className="flex justify-between">
                                <div className="text-base/5">{callTitle}</div>
                                <div
                                    className={twMerge(
                                        'flex gap-2',
                                        contextMenuIsActive
                                            ? 'hidden'
                                            : 'group-focus-visible-within:hidden group-hover:hidden',
                                    )}
                                >
                                    <div className="flex items-center gap-2">
                                        {voiceMessage.get(
                                            'voiceMessageState',
                                        ) === 'unread' && (
                                            <div className="bg-red size-2.5 rounded-full" />
                                        )}
                                        {voiceMessagesActions?.get(
                                            voiceMessage.get('uniqueId'),
                                        ) && (
                                            <Icon
                                                iconKey="success"
                                                className="text-green"
                                                size="1rem"
                                            />
                                        )}

                                        <div className="text-xs text-gray-500">
                                            {moment(
                                                voiceMessage.get(
                                                    'receivedDate',
                                                ),
                                            ).format('HH:mm')}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <span className="flex gap-x-2 text-sm text-gray-500">
                                {getLoggedCallSubtitle(
                                    voiceMessage,
                                    i18next,
                                    ownCountryCode,
                                    callTitle,
                                )}
                            </span>
                        </div>

                        <div
                            className={twMerge(
                                'shrink gap-1',
                                contextMenuIsActive
                                    ? 'flex'
                                    : 'sr-only-until-hover-or-focus flex',
                            )}
                        >
                            {voiceMessagesActions?.get(
                                voiceMessage.get('uniqueId'),
                            ) && (
                                <IconButton
                                    tooltipLabel={getHandledInfo()}
                                    // The tooltip carries who handled it and
                                    // when; when that is not known there is no
                                    // tooltip, and the button still needs a
                                    // name.
                                    aria-label={
                                        getHandledInfo() ??
                                        i18next.t('call.handled')
                                    }
                                    className="text-green"
                                >
                                    <Icon iconKey="success" />
                                </IconButton>
                            )}

                            <CallAdaptiveButton
                                phoneNumber={voiceMessage.get('number')}
                            />

                            {/*
                             * A link, not a link wrapping a button: nesting
                             * two interactive elements is invalid markup and
                             * announced as a link containing a button, with
                             * neither carrying a name.
                             */}
                            <Tooltip
                                label={i18next.t('Tooltip.Audio.Download')}
                            >
                                <a
                                    href={sharedVoiceMessageUrlPath(
                                        voicemailKey,
                                        voiceMessage.get('uniqueId'),
                                    )}
                                    download={true}
                                    onClick={() => setPreventUnload(true)}
                                    aria-label={i18next.t(
                                        'Tooltip.Audio.Download',
                                    )}
                                    className="hover:darken-10 flex items-center justify-center rounded-sm border-none bg-gray-200 p-1"
                                >
                                    <Icon iconKey="get_app" />
                                </a>
                            </Tooltip>

                            <IconButton
                                onClick={() =>
                                    dispatch(
                                        removeSharedVoiceMessage(
                                            voicemailKey,
                                            voiceMessage.get('uniqueId'),
                                        ),
                                    )
                                }
                                tooltipLabel={i18next.t('Tooltip.Audio.Delete')}
                                className="bg-gray-200"
                            >
                                <Icon
                                    className="cursor-pointer"
                                    iconKey="delete"
                                />
                            </IconButton>

                            <PbxVoiceMessageContextMenu
                                voiceMessage={voiceMessage}
                                entityKey={voicemailKey}
                                voiceMessagesAction={voiceMessagesActions?.get(
                                    voiceMessage.get('uniqueId'),
                                )}
                                setContextMenuIsActive={setContextMenuIsActive}
                            />
                        </div>
                    </div>

                    <AudioPlayer
                        duration={voiceMessage.get('durationInSeconds')}
                        uniqueId={voiceMessage.get('uniqueId')}
                        url={sharedVoiceMessageUrlPath(
                            voicemailKey,
                            voiceMessage.get('uniqueId'),
                        )}
                    />
                </div>
            </div>
        </div>
    );
};

export default PbxSharedVoicemailItem;
